import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { openCart, showBanner } from '@store/redux/actions/otherActions'
import addToCart from '@store/redux/actions/addToCart'
import { checkStoreIs } from '@helpers/env'
import { scrollToTop } from '@helpers/utils'
import { createCartItem, isCartItemOutOfStock } from '@helpers/cart'
import {
  productViewedEvent,
  getProductCartItems,
  productSkuViewedEvent,
  combineVariantsInventoryLevel
} from '@helpers/products'
import { getSelectedFilterOptionsForProduct } from '@helpers/products/getSelectedFilterOptions'
import { getVariantItem } from '@helpers/filters'
import { analytics } from '@helpers/analytics'
import { ProductWithVariants } from 'types/product'
import { CustomAttribute, CartLineItem } from 'types/checkout'
import { useFit } from '@helpers/hooks/useFit'
import BackInStockModal from '@components/product/BackInStockModal'
import { SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'
import { TrustpilotRatings } from '@api/trustpilot'
import AddToCartButton from './AddToCartButton'
import Sezzle from './externalWidgets/Sezzle'
import ProductInfo from './ProductInfo'
import ProductHeader from './ProductHeader'
import Filters from './Filters'
import ProductSwatches from './ProductSwatches'
import ProductGallery from './ProductGallery'
import SweetenersList from './SweetenersList'
import StickyContainer from './AddToCartButton/StickyContainer'
import StickyAddToCart from './AddToCartButton/StickyAddToCart'
import styles from './styles.module.scss'
import FulfillmentInfo, { getFulfillmentConfig } from './mto/FulfillmentInfo'
import { FULFILLMENT_LOCATION_FLAG } from '@consts/index'
import { ShopifyCartError } from '@api/graphqlShopify/types'
import { NotificationKey } from '@components/FlashBanner/PushNotificationBanner'
import { isBlockingCartMutationError, useCart } from '@components/cart/hooks'
import { useMtoBundle } from './mto/useMtoBundle'
import { isBundleProduct } from './mto/helpers'
import ProductContext from './context'
import CompleteTheLook from './CompleteTheLook'
import { CompleteTheLookProductsByHandle } from '@lib/completeTheLook'

type ErrorWithMessage = { message: string }

const ERROR_TO_BANNER = {
  'Variant is invalid': 'Add to basket - variant problem',
  'Out of stock': 'Add to basket - Out of stock'
} as const satisfies Record<string, NotificationKey>

const getFirstErrorMessage = (e: unknown): string => {
  if (Array.isArray(e))
    return (e[0] as ErrorWithMessage | undefined)?.message ?? ''
  if (e && typeof e === 'object' && 'message' in e)
    return String(e.message ?? '')
  return ''
}

const isCartBlockingError = (e: unknown): boolean => {
  if (!Array.isArray(e)) return false
  const first = e[0]
  return isBlockingCartMutationError(first as ShopifyCartError)
}

function lineItemsContainMatchingVariantSizes({
  lineItems,
  latestLineItemVariantId,
  latestLineItemCustomAttributes
}: {
  lineItems: CartLineItem[]
  latestLineItemVariantId: string | undefined
  latestLineItemCustomAttributes: CustomAttribute[]
}) {
  if (
    lineItems.length === 0 ||
    !latestLineItemVariantId ||
    latestLineItemCustomAttributes.length === 0
  ) {
    return false
  }

  const matchingLineItemSizeAttributes = lineItems.filter(line => {
    const cartItemVariantId = line.merchandise.id
    if (cartItemVariantId === latestLineItemVariantId) {
      const filteredLineAttributes = line.attributes.filter(
        attr =>
          !['_addedToCartTimestamp', FULFILLMENT_LOCATION_FLAG].includes(
            attr.key
          )
      )
      return filteredLineAttributes.every(lineItemAttribute => {
        const variantAttribute = latestLineItemCustomAttributes.filter(
          customAttr => customAttr.key === lineItemAttribute.key
        )
        return variantAttribute[0].value === lineItemAttribute.value
      })
    }
  })

  return matchingLineItemSizeAttributes.length > 0
}

type ProductProps = {
  productColor: string
  selectedProduct: ProductWithVariants
  productHandle: string
  byline: string
  variantExist: boolean
  hasSizeChartData?: boolean
  trustpilotRatings?: TrustpilotRatings | null
  onOpenDrawer?: () => void
  completeTheLookProductsByHandle?: CompleteTheLookProductsByHandle
}

function Product({
  productColor,
  productHandle,
  byline,
  variantExist,
  selectedProduct,
  hasSizeChartData = true,
  trustpilotRatings,
  onOpenDrawer,
  completeTheLookProductsByHandle = {}
}: ProductProps) {
  const [openOOSModal, setOpenOOSModal] = useState(false)
  const toggleOOSModal = () => setOpenOOSModal(!openOOSModal)
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false)

  const { tabs } = useContext(ProductContext)

  const { cart, updateCartQuantity } = useCart()

  const dispatch = useDispatch()
  const { filters, savedFit } = useFit()

  const filterOptions = filters

  const selectedFilterOptions = useMemo(
    () => getSelectedFilterOptionsForProduct(selectedProduct, filterOptions),
    [selectedProduct, filterOptions]
  )

  const selectedOptionsString = selectedFilterOptions
    .map(option => option.value)
    .join(' / ')

  const selectedVariant = useMemo(() => {
    if (!variantExist) {
      return []
    }
    return getVariantItem(selectedProduct, selectedFilterOptions) || []
  }, [variantExist, selectedProduct, selectedFilterOptions])

  const productTags = selectedProduct?.tags.split(',')

  const hasProductVariants = !!selectedProduct.variants
  const hasAllVariantOptionsSelected =
    selectedProduct.options.length === selectedFilterOptions.length

  const emptyOptions = selectedProduct.options?.filter(
    productOption =>
      !selectedFilterOptions.find(
        filterOption => filterOption.type === productOption.type
      )
  )

  const selectedVariants = useMemo(
    () => getVariantItem(selectedProduct, selectedFilterOptions) || [],
    [selectedProduct, selectedFilterOptions]
  )

  const isSelectedProductOutOfStock = variantExist && !selectedVariants.length
  const isSelectedProductReturnable =
    !selectedProduct.tags.includes('non-returnable')

  const isActive =
    hasProductVariants &&
    selectedVariant.length > 0 &&
    hasAllVariantOptionsSelected &&
    selectedVariant[0].inventory_quantity > 0

  const isBundle = isBundleProduct(selectedProduct.product_feed?.bundle_name)

  const fulfillment = hasAllVariantOptionsSelected
    ? getFulfillmentConfig({
        sku: selectedProduct?.product_feed?.group_id,
        fulfillmentLocation: selectedVariant[0]?.fulfillment_location
      })
    : null

  const { ctaLabel, handleBundleClick } = useMtoBundle({
    isMtoBundle: isBundle,
    selectedProduct,
    selectedVariant: selectedVariant[0],
    selectedFilterOptions,
    tabs,
    cartLines: cart.lines
  })

  useEffect(() => {
    if (variantExist) {
      productViewedEvent(selectedProduct, savedFit)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct.id, variantExist])

  useEffect(() => {
    if (hasAllVariantOptionsSelected && selectedVariant.length) {
      const productOptionsAsObject = selectedFilterOptions.reduce(
        (result, option) => ({
          ...result,
          [option.type]: option.value
        }),
        {}
      )

      const { sku } = selectedVariant[0] || {}

      const productInventory = combineVariantsInventoryLevel(selectedVariant)

      productSkuViewedEvent(selectedProduct, savedFit, {
        ...productOptionsAsObject,
        sku,
        variant: selectedOptionsString,
        availability: 'in stock',
        inventory: productInventory
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAllVariantOptionsSelected,
    selectedProduct.id,
    selectedOptionsString,
    selectedVariant.length,
    variantExist
  ])

  useEffect(() => {
    if (isSelectedProductOutOfStock || openOOSModal) {
      analytics('Clicked out of stock')
    }
  }, [isSelectedProductOutOfStock, openOOSModal])

  const handleNormalAdd = async ({
    triggerButtonSuccess,
    triggerButtonError
  }: SubmitButtonHandlers) => {
    try {
      const {
        variantId,
        customAttributes,
        lineItemInCart,
        fulfillmentLocation
      } = createCartItem(selectedProduct, selectedFilterOptions, cart.lines)

      if (
        isCartItemOutOfStock({
          lineItemInCart,
          fulfillmentLocation,
          cartLines: cart.lines,
          productId: selectedProduct.id
        })
      ) {
        throw [{ message: 'Out of stock' }]
      }

      const lineItemsVariantSizesMatch = lineItemsContainMatchingVariantSizes({
        lineItems: cart.lines,
        latestLineItemVariantId: lineItemInCart.lineItemVariantId,
        latestLineItemCustomAttributes: customAttributes
      })

      if (
        lineItemInCart.lineItemId &&
        lineItemInCart.lineItemVariantId &&
        lineItemInCart.quantityInCart > 0 &&
        lineItemsVariantSizesMatch
      ) {
        await updateCartQuantity(
          lineItemInCart.lineItemId,
          lineItemInCart.lineItemVariantId,
          lineItemInCart.quantityInCart + 1,
          'Product Page'
        )
      } else {
        const cartItems = getProductCartItems({
          quantity: 1,
          variantId: `gid://shopify/ProductVariant/${variantId}`,
          customAttributes,
          fulfillmentLocation
        })
        await dispatch(addToCart(cartItems, 'Product Page'))
      }

      triggerButtonSuccess()
      dispatch(openCart())
    } catch (e) {
      const message = getFirstErrorMessage(e)

      const bannerType: NotificationKey =
        ERROR_TO_BANNER[message as keyof typeof ERROR_TO_BANNER] ??
        (isCartBlockingError(e)
          ? 'Fix Checkout Details'
          : 'Add to basket - failed')

      dispatch(
        showBanner({
          isVisible: true,
          type: bannerType,
          theme: 'error'
        })
      )

      triggerButtonError()
      scrollToTop()

      setTimeout(() => {
        dispatch(showBanner({ isVisible: false }))
      }, 10000)
    }
  }

  const onAddToCart = isBundle ? handleBundleClick : handleNormalAdd

  const showSezzleWidget = !!selectedProduct.price && checkStoreIs('us')

  if (!selectedProduct) return null

  return (
    <>
      <div data-testid="product-page" className={styles.productWrapper}>
        <div
          className={`${styles.productImages} ${
            isImageGalleryOpen ? styles.imageGalleryOpen : ''
          }`}
        >
          <ProductHeader
            product={selectedProduct}
            byline={byline}
            starsAverage={trustpilotRatings?.starsAverage}
            totalReviews={trustpilotRatings?.numberOfReviews.total}
            classes={styles.sidebar__productHeader}
            onStarsClick={onOpenDrawer}
          />
          <div className={styles.galleryWrapper}>
            <ProductGallery
              title={selectedProduct.title}
              images={selectedProduct.images}
              setIsImageGalleryOpen={setIsImageGalleryOpen}
            />
          </div>
        </div>
        <div className={styles.productForm__content}>
          <ProductHeader
            product={selectedProduct}
            byline={byline}
            starsAverage={trustpilotRatings?.starsAverage}
            totalReviews={trustpilotRatings?.numberOfReviews.total}
            classes={styles.sidebar__productHeader}
            onStarsClick={onOpenDrawer}
          />
          <div className={styles.product__swatchesContainer}>
            <ProductSwatches
              tags={productTags}
              selectedProduct={selectedProduct}
              selectedFilterOptions={selectedFilterOptions}
            />
          </div>
          <Filters
            product={selectedProduct}
            filterOptions={filterOptions}
            variantExist={variantExist}
            selectedOptions={selectedFilterOptions}
            hasSizeChartData={hasSizeChartData}
          />
          {fulfillment && (
            <FulfillmentInfo
              classes={styles.productInfo__mto}
              fulfillment={fulfillment}
            />
          )}
          <AddToCartButton
            active={isActive}
            label={ctaLabel}
            handle={productHandle}
            onAddToCart={onAddToCart}
            onOutOfStock={toggleOOSModal}
            isSelectedProductOutOfStock={isSelectedProductOutOfStock}
          />
          {isSelectedProductReturnable && <SweetenersList />}
          {showSezzleWidget && <Sezzle price={selectedProduct.price} />}
          <CompleteTheLook
            sourceHandle={productHandle}
            productsByHandle={completeTheLookProductsByHandle}
          />
          <ProductInfo
            text={selectedProduct.body_html}
            toggleOOSModal={toggleOOSModal}
            product={selectedProduct}
          />
        </div>
      </div>
      {openOOSModal && (
        <BackInStockModal
          onClose={toggleOOSModal}
          id={selectedProduct.id}
          options={selectedProduct.options}
          productType={selectedProduct.product_type}
          productFeed={selectedProduct.product_feed}
          title={selectedProduct.title}
          image={selectedProduct.image}
          variants={selectedProduct.variants}
        />
      )}
      <StickyContainer
        isImageGalleryOpen={isImageGalleryOpen}
        hasEmptyOptions={!!emptyOptions.length}
        openOOSModal={openOOSModal}
      >
        <StickyAddToCart
          label={ctaLabel}
          isSelectedProductOutOfStock={isSelectedProductOutOfStock}
          selectedProduct={selectedProduct}
          handle={productHandle}
          onOutOfStock={toggleOOSModal}
          onAddToCart={onAddToCart}
          productColor={productColor}
          hasAllVariantOptionsSelected={hasAllVariantOptionsSelected}
          emptyOptions={emptyOptions}
          active={isActive}
        />
      </StickyContainer>
    </>
  )
}

export default Product
